import { useEffect, useState } from 'react';
import { ArrowDownToLine, ExternalLink, RefreshCw } from 'lucide-react';
import { exportPath, request } from '../lib/api';
import './SourceSheets.css';

type Sheet = { name: string; total: number; matched: number; positive: number; negative: number; failed: number; suppressed: number };
type Summary = { available: boolean; rows: Sheet[]; message?: string };

export function SourceSheets({ jobId, onOpen, onDownload }: {
  jobId: string;
  onOpen: (sheet: string) => void;
  onDownload: (path: string, name: string) => Promise<void>;
}) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [sort, setSort] = useState<'positive' | 'negative' | 'total'>('positive');
  const [format, setFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [downloading, setDownloading] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setSummary(null);
    setError('');
    request<Summary>(`/api/sources/${jobId}/sheets`).then(data => { if (active) setSummary(data); })
      .catch(reason => { if (active) setError(reason instanceof Error ? reason.message : 'Could not load sheets.'); });
    return () => { active = false; };
  }, [jobId, revision]);
  const rows = [...(summary?.rows ?? [])].sort((a, b) => b[sort] - a[sort] || a.name.localeCompare(b.name));
  async function download(sheet: Sheet) {
    setDownloading(sheet.name);
    try {
      await onDownload(exportPath({ sourceJobId: jobId, sourceSheet: sheet.name, limit: 250 }, format), `${sheet.name}.${format}`);
    } finally { setDownloading(null); }
  }
  return <section className="source-sheet-results" aria-label="Worksheet results">
    <div className="source-sheet-toolbar"><h3>Worksheets</h3>
      <label>Rank by <select value={sort} onChange={event => setSort(event.target.value as typeof sort)}>
        <option value="positive">Positive replies</option><option value="negative">Negative replies</option><option value="total">Unique emails</option>
      </select></label>
      <label>Export <select value={format} onChange={event => setFormat(event.target.value as typeof format)}><option value="xlsx">XLSX</option><option value="csv">CSV</option></select></label>
      <button type="button" className="ghost-button" title="Refresh worksheet results" aria-label="Refresh worksheet results" onClick={() => setRevision(value => value + 1)}><RefreshCw size={16} /></button>
    </div>
    {error ? <p role="alert">{error}</p> : !summary ? <p role="status">Loading worksheet results...</p> : !summary.available ? <p>{summary.message}</p> : !rows.length ? <p>No valid emails in this import.</p> :
      <div className="source-sheet-scroll"><table><thead><tr><th>Sheet</th><th>Unique emails</th><th>Matched leads</th><th>Positive</th><th>Negative</th><th>Failed</th><th>Suppressed</th><th>Actions</th></tr></thead>
        <tbody>{rows.map(sheet => <tr key={sheet.name}><td>{sheet.name}</td>{(['total', 'matched', 'positive', 'negative', 'failed', 'suppressed'] as const).map(key => <td key={key}>{sheet[key].toLocaleString()}</td>)}
          <td><div className="source-sheet-actions"><button type="button" className="ghost-button" title={`Open ${sheet.name}`} aria-label={`Open ${sheet.name}`} onClick={() => onOpen(sheet.name)}><ExternalLink size={16} /></button>
            <button type="button" className="ghost-button" disabled={downloading !== null} title={`Export ${sheet.name}`} aria-label={`Export ${sheet.name}`} onClick={() => void download(sheet)}><ArrowDownToLine size={16} /></button></div></td>
        </tr>)}</tbody></table></div>}
  </section>;
}
